// Filtros del Dashboard de Adquisición (Fuente / Medio / Campaña).
//
// Las filas ya vienen agrupadas por (fuente, medio, campaña) desde
// admin_utm_acquisition_summary, así que filtrar filas y sumar sus métricas da
// totales exactos para la selección. Dentro de una dimensión se combina con OR
// (brevo O ig); entre dimensiones con AND (brevo Y email Y PRUEBA).

export interface AcquisitionRowLike {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  is_direct: boolean;
  visitors: number;
  signups: number;
  diagnostics: number;
}

export type AcquisitionDimension = 'source' | 'medium' | 'campaign';

export interface AcquisitionFilters {
  source: string[];
  medium: string[];
  campaign: string[];
}

/** Valor especial para el tráfico directo (sin ningún UTM). */
export const DIRECT_KEY = '__direct__';
/** Valor especial para filas con UTMs pero sin este parámetro concreto. */
export const NONE_KEY = '__none__';

export const EMPTY_FILTERS: AcquisitionFilters = { source: [], medium: [], campaign: [] };

const COLUMN: Record<AcquisitionDimension, 'utm_source' | 'utm_medium' | 'utm_campaign'> = {
  source: 'utm_source',
  medium: 'utm_medium',
  campaign: 'utm_campaign',
};

export function dimensionValue(row: AcquisitionRowLike, dim: AcquisitionDimension): string {
  if (row.is_direct) return DIRECT_KEY;
  return row[COLUMN[dim]] ?? NONE_KEY;
}

function matches(row: AcquisitionRowLike, filters: AcquisitionFilters, skip?: AcquisitionDimension): boolean {
  return (Object.keys(COLUMN) as AcquisitionDimension[]).every(dim => {
    if (dim === skip) return true;
    const selected = filters[dim];
    return selected.length === 0 || selected.includes(dimensionValue(row, dim));
  });
}

export function filterAcquisitionRows<T extends AcquisitionRowLike>(rows: T[], filters: AcquisitionFilters): T[] {
  return rows.filter(r => matches(r, filters));
}

export function sumAcquisition(rows: AcquisitionRowLike[]) {
  return rows.reduce(
    (acc, r) => ({
      visitors: acc.visitors + r.visitors,
      signups: acc.signups + r.signups,
      diagnostics: acc.diagnostics + r.diagnostics,
    }),
    { visitors: 0, signups: 0, diagnostics: 0 },
  );
}

/**
 * Opciones disponibles para una dimensión, considerando los filtros de las OTRAS
 * dimensiones (filtros cruzados: al elegir Fuente=brevo, Campaña solo ofrece
 * campañas de brevo). Los valores ya seleccionados siempre se conservan.
 */
export function dimensionOptions(
  rows: AcquisitionRowLike[],
  dim: AcquisitionDimension,
  filters: AcquisitionFilters,
): { value: string; signups: number }[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!matches(r, filters, dim)) continue;
    const v = dimensionValue(r, dim);
    counts.set(v, (counts.get(v) ?? 0) + r.signups);
  }
  for (const v of filters[dim]) if (!counts.has(v)) counts.set(v, 0);
  return [...counts.entries()]
    .map(([value, signups]) => ({ value, signups }))
    .sort((a, b) => b.signups - a.signups || a.value.localeCompare(b.value));
}
